import React, { useEffect } from "react";
import UserScreenWindow from "../Components/UserScreenWindow";
import { getAllDocs } from "../../../utils/firebase/Firebase.utils";
import RequestsCard from "../WorkPermit/RequestsCard";
import { useSelector } from "react-redux";
import { Link } from "react-router-dom";
import GatePassRequestCard from "../Gatepass/GatePassRequestCard";
import ConcernsRequestCard from "../Concerns/ConcernsRequestCard";
import {
  FiFileText,
  FiShield,
  FiHome,
  FiAlertCircle,
  FiList,
} from "react-icons/fi";
import { motion } from "framer-motion";
import BookingRequestCard from "../Facilities/BookingRequestCard";

function RequestSummary() {
  const [requests, setRequests] = React.useState([]);
  const [isLoading, setIsLoading] = React.useState(true);
  const currentUser = useSelector((state) => state.userObject);
  const [activeLink, setActiveLink] = React.useState("workPermits");
  const [gatePasses, setGatePasses] = React.useState([]);
  const [concerns, setConcerns] = React.useState([]);
  const [bookings, setBookings] = React.useState([]);

  const links = [
    {
      name: "Work Permits",
      link: "workPermits",
      icon: <FiFileText className="mr-2" />,
    },
    {
      name: "Gate Pass",
      link: "gatePass",
      icon: <FiShield className="mr-2" />,
    },
    {
      name: "Facilities",
      link: "facilities",
      icon: <FiHome className="mr-2" />,
    },
    {
      name: "Concerns",
      link: "concerns",
      icon: <FiAlertCircle className="mr-2" />,
    },
  ];

  useEffect(() => {
    const fetchRequests = async () => {
      const data = await getAllDocs("concerns");
      //filter active requests by email
      const userRequests = data.filter(
        (request) => request.user === currentUser.email
      );
      setConcerns(userRequests);
    };
    fetchRequests();
  }, []);

  useEffect(() => {
    const fetchRequests = async () => {
      const data = await getAllDocs("workPermits");
      //filter active requests by email
      const userRequests = data.filter(
        (request) => request.createdBy === currentUser.email
      );
      setRequests(userRequests);
    };
    fetchRequests();
  }, []);

  useEffect(() => {
    const fetchGatePasses = async () => {
      const data = await getAllDocs("gatePass");
      //
      const userGatePasses = data.filter(
        (request) => request.user === currentUser.email
      );
      setGatePasses(userGatePasses);
      setIsLoading(false);
    };
    fetchGatePasses();
  }, []);

  useEffect(() => {
    const fetchBookings = async () => {
      const data = await getAllDocs("bookings");
      //
      const bookingArray = data.filter(
        (request) => request.user === currentUser.email
      );
      setBookings(bookingArray);
      setIsLoading(false);
    };
    fetchBookings();
  }, []);

  if (isLoading) {
    return (
      <UserScreenWindow label="Requests Summary">
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#363062]"></div>
        </div>
      </UserScreenWindow>
    );
  }

  const getCurrentItems = () => {
    switch (activeLink) {
      case "workPermits":
        return requests;
      case "gatePass":
        return gatePasses;
      case "facilities":
        return bookings;
      case "concerns":
        return concerns;
      default:
        return [];
    }
  };

  return (
    <UserScreenWindow label="Requests Summary">
      <div className="w-full mx-auto px-4 max-w-4xl">
        <div className="flex items-center justify-between mb-6 bg-[#363062]/5 p-4 rounded-lg">
          <div className="flex items-center space-x-3">
            <div className="p-2 bg-[#363062] rounded-lg">
              <FiList className="text-white text-base" />
            </div>
            <div>
              <h1 className="text-sm font-medium text-[#363062]">
                Requests Summary
              </h1>
              <p className="text-xs text-gray-500">
                View and manage all your requests in one place
              </p>
            </div>
          </div>
        </div>

        <div className="mb-6 -mx-4 relative">
          <div className="overflow-x-auto px-4 pr-20 flex no-scrollbar relative">
            <div className="flex space-x-2 ">
              {links.map((link, index) => (
                <button
                  key={link.name}
                  className={`flex items-center whitespace-nowrap px-4 py-2 rounded-full text-xs font-medium transition-all ${
                    activeLink === link.link
                      ? "bg-[#363062] text-white shadow-sm"
                      : "bg-gray-50 text-[#363062] hover:bg-[#363062]/5"
                  }`}
                  onClick={() => setActiveLink(link.link)}
                >
                  {link.icon}
                  <span>{link.name}</span>
                </button>
              ))}
            </div>
          </div>
          <div className="absolute right-0 top-0 h-full w-20 bg-gradient-to-l from-gray-100 via-gray-100 to-transparent pointer-events-none"></div>
        </div>

        <div className="space-y-3">
          {activeLink === "workPermits" && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="space-y-3"
            >
              {requests.map((request, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.1 }}
                >
                  <Link
                    to={`/request-details/${request.id}`}
                    className="block transition-all hover:transform hover:translate-y-[-2px] hover:shadow-md"
                  >
                    <RequestsCard request={request} />
                  </Link>
                </motion.div>
              ))}
            </motion.div>
          )}

          {activeLink === "gatePass" && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="space-y-3"
            >
              {gatePasses.map((request, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.1 }}
                >
                  <Link
                    to={`/gatepass-details/${request.id}`}
                    className="block transition-all hover:transform hover:translate-y-[-2px] hover:shadow-md"
                  >
                    <GatePassRequestCard gatepass={request} />
                  </Link>
                </motion.div>
              ))}
            </motion.div>
          )}

          {activeLink === "facilities" && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="space-y-3"
            >
              {bookings.map((booking, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.1 }}
                >
                  <Link
                    to={`/booking/${booking.id}`}
                    className="block transition-all hover:transform hover:translate-y-[-2px] hover:shadow-md"
                  >
                    <BookingRequestCard booking={booking} />
                  </Link>
                </motion.div>
              ))}
            </motion.div>
          )}

          {activeLink === "concerns" && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="space-y-3"
            >
              {concerns.map((request, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.1 }}
                >
                  <Link
                    to={`/concern/${request.id}`}
                    className="block transition-all hover:transform hover:translate-y-[-2px] hover:shadow-md"
                  >
                    <ConcernsRequestCard concern={request} />
                  </Link>
                </motion.div>
              ))}
            </motion.div>
          )}

          {getCurrentItems().length === 0 && !isLoading && (
            <div className="text-center py-8 bg-gray-50 rounded-lg border border-gray-100">
              <div className="p-3 bg-[#8FCD00]/10 rounded-full w-12 h-12 mx-auto mb-3 flex items-center justify-center">
                <FiFileText className="text-[#8FCD00] text-lg" />
              </div>
              <h3 className="text-sm font-medium text-[#363062] mb-1">
                No Requests Found
              </h3>
              <p className="text-xs text-gray-500">
                You don't have any{" "}
                {activeLink.replace(/([A-Z])/g, " $1").toLowerCase()} requests
                at the moment
              </p>
            </div>
          )}
        </div>
      </div>
    </UserScreenWindow>
  );
}

export default RequestSummary;